import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import { useChatService } from '../service/chatService';
import PropTypes from 'prop-types';

const ChatContext = createContext(null);

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const socket = useSocket();
  const { user } = useAuth();
  const chatService = useChatService();
  const isAuthenticated = !!user;

  // Estado principal
  const [conversations, setConversations] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [globalMessages, setGlobalMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});

  // Datos de roles y usuarios
  const [roleGroups, setRoleGroups] = useState([]);
  const [currentUserRoles, setCurrentUserRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Control de carga
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Cargar conversaciones
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingConversations(true);
    try {
      const result = await chatService.getRecentConversations(30, 0);
      setConversations(result.data || []);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [isAuthenticated]);

  // Cargar mensajes de una conversacion
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId || conversationId === 'global') return;
    setIsLoadingMessages(true);
    try {
      const result = await chatService.getConversationMessages(conversationId, 50);
      setMessages(result.data || []);
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  // Cargar usuarios por rol
  const loadUsersByRole = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await chatService.getUsersByRole();
      if (result.data) {
        setRoleGroups(result.data.roles || []);
        setCurrentUserRoles(result.data.currentUserRoles || []);
        // Priorizar el primer rol del usuario actual
        if (result.data.currentUserRoles?.length > 0 && !selectedRoleId) {
          setSelectedRoleId(result.data.currentUserRoles[0].id);
        }
      }
    } catch (error) {
      console.error('Error cargando usuarios por rol:', error);
    }
  }, [isAuthenticated]);

  // Cargar datos iniciales
  useEffect(() => {
    if (!isAuthenticated) return;
    loadConversations();
    loadUsersByRole();
  }, [isAuthenticated, loadConversations, loadUsersByRole]);

  // Cargar mensajes cuando cambia el chat activo
  useEffect(() => {
    if (!activeChatId || activeChatId === 'global') {
      setMessages([]);
      return;
    }
    loadMessages(activeChatId);
  }, [activeChatId, loadMessages]);

  // Marcar como leido al abrir conversacion
  useEffect(() => {
    if (!activeChatId || activeChatId === 'global' || !isAuthenticated) return;
    const timer = setTimeout(() => {
      if (socket) {
        socket.emit('chat:mark-read', { conversationId: activeChatId });
      }
      // Actualizar conteo local
      setConversations(prev => prev.map(c =>
        c.conversation_id === activeChatId ? { ...c, unread_count: '0' } : c
      ));
    }, 500);
    return () => clearTimeout(timer);
  }, [activeChatId, isAuthenticated, socket]);

  // --- Socket events ---
  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    // Nuevo mensaje privado
    const handleNewMessage = (data) => {
      const convId = data.conversationId || data.conversation_id;
      const isActive = convId === activeChatId;

      // Si estamos en esa conversacion, agregar al listado y marcar leido
      if (isActive) {
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          return [...prev, data];
        });
        socket.emit('chat:mark-read', { conversationId: convId });
      }

      // Actualizar sidebar siempre (ultimo mensaje, fecha, y no leidos si no es activa)
      setConversations(prev => {
        const exists = prev.find(c => c.conversation_id === convId);
        if (!exists) {
          loadConversations();
          return prev;
        }
        const updated = prev.map(c => {
          if (c.conversation_id !== convId) return c;
          return {
            ...c,
            last_message_content: data.content || data.original_content,
            message_date: data.created_at,
            unread_count: isActive ? '0' : String(parseInt(c.unread_count || '0') + 1),
          };
        });
        return [...updated].sort((a, b) => {
          const dateA = a.message_date ? new Date(a.message_date).getTime() : 0;
          const dateB = b.message_date ? new Date(b.message_date).getTime() : 0;
          return dateB - dateA;
        });
      });
    };

    // Mensaje editado
    const handleMessageEdited = (data) => {
      setMessages(prev => prev.map(m => m.id === data.id ? { ...m, ...data } : m));
    };

    // Mensaje eliminado
    const handleMessageDeleted = (data) => {
      setMessages(prev => prev.filter(m => m.id !== data.messageId));
    };

    // Usuarios en linea
    const handleOnlineUsers = (userIds) => {
      setOnlineUsers(userIds || []);
    };

    // Typing
    const handleUserTyping = ({ userId, conversationId }) => {
      setTypingUsers(prev => {
        const current = prev[conversationId] || [];
        if (current.includes(userId)) return prev;
        return { ...prev, [conversationId]: [...current, userId] };
      });
    };

    const handleUserStopTyping = ({ userId, conversationId }) => {
      setTypingUsers(prev => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter(id => id !== userId),
      }));
    };

    // Conversacion leida por otro usuario
    const handleConversationRead = ({ conversationId, userId: readByUserId }) => {
      if (readByUserId !== user.id) {
        // El otro usuario leyo la conversacion
      }
    };

    // Mensajes del chat global
    const handleGlobalMessage = (data) => {
      setGlobalMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, data];
      });
    };

    socket.on('chat:new-message', handleNewMessage);
    socket.on('chat:message-edited', handleMessageEdited);
    socket.on('chat:message-deleted', handleMessageDeleted);
    socket.on('chat:online-users', handleOnlineUsers);
    socket.on('chat:user-typing', handleUserTyping);
    socket.on('chat:user-stop-typing', handleUserStopTyping);
    socket.on('chat:conversation-read', handleConversationRead);
    socket.on('chat:global:message', handleGlobalMessage);

    // Solicitar lista actual al registrar los listeners (cubre el caso de recarga)
    socket.emit('chat:request-online-users', (response) => {
      if (response?.data) setOnlineUsers(response.data);
    });

    return () => {
      socket.off('chat:new-message', handleNewMessage);
      socket.off('chat:message-edited', handleMessageEdited);
      socket.off('chat:message-deleted', handleMessageDeleted);
      socket.off('chat:online-users', handleOnlineUsers);
      socket.off('chat:user-typing', handleUserTyping);
      socket.off('chat:user-stop-typing', handleUserStopTyping);
      socket.off('chat:conversation-read', handleConversationRead);
      socket.off('chat:global:message', handleGlobalMessage);
    };
  }, [socket, isAuthenticated, activeChatId, user, loadConversations]);

  // --- Acciones ---

  // Enviar mensaje privado via socket
  const sendMessage = useCallback(async (content, files = [], replyToMessageId = null) => {
    if (!isAuthenticated) return;
    if (!content?.trim() && files.length === 0) return;

    // Si hay archivos, usar REST
    if (files.length > 0) {
      try {
        await chatService.sendMessage({
          conversationId: activeChatId !== 'global' ? activeChatId : undefined,
          content: content?.trim() || '',
          files,
          replyToMessageId,
          recipientId: !activeChatId ? selectedRecipient?.id : undefined,
        });
      } catch (error) {
        console.error('Error enviando mensaje con archivos:', error);
      }
      return;
    }

    // Chat global via socket
    if (activeChatId === 'global') {
      if (socket && content?.trim()) {
        socket.emit('chat:global:send', { content: content.trim() }, (response) => {
          if (!response.success) {
            console.error('Error enviando mensaje global:', response.error);
          }
        });
      }
      return;
    }

    // Chat privado via socket
    if (socket) {
      socket.emit('chat:send-message', {
        conversationId: activeChatId || null,
        recipientId: !activeChatId ? selectedRecipient?.id : null,
        content: content.trim(),
        replyToMessageId,
      }, (response) => {
        if (response.success && !activeChatId && response.conversationId) {
          // Nueva conversacion creada
          setActiveChatId(response.conversationId);
          loadConversations();
        }
        if (!response.success) {
          console.error('Error enviando mensaje:', response.error);
        }
      });
    }
  }, [isAuthenticated, activeChatId, selectedRecipient, socket, loadConversations]);

  // Editar mensaje
  const editMessage = useCallback(async (messageId, newContent) => {
    if (!isAuthenticated) return;
    try {
      await chatService.editMessage(messageId, newContent);
    } catch (error) {
      console.error('Error editando mensaje:', error);
    }
  }, [isAuthenticated]);

  // Eliminar mensaje
  const deleteMessage = useCallback(async (messageId) => {
    if (!isAuthenticated) return;
    try {
      await chatService.deleteMessage(messageId);
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
    }
  }, [isAuthenticated]);

  // Emitir typing
  const emitTyping = useCallback((conversationId) => {
    if (!socket || !conversationId || conversationId === 'global') return;
    socket.emit('chat:typing', { conversationId });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:stop-typing', { conversationId });
    }, 2000);
  }, [socket]);

  // Unirse al chat global
  const joinGlobalChat = useCallback(() => {
    if (!socket) return;
    socket.emit('chat:global:join', (response) => {
      if (response?.success) {
        setGlobalMessages(response.data || []);
      }
    });
  }, [socket]);

  // Salir del chat global
  const leaveGlobalChat = useCallback(() => {
    if (!socket) return;
    socket.emit('chat:global:leave');
  }, [socket]);

  // Seleccionar un usuario para chatear
  const selectUser = useCallback(async (selectedUser) => {
    setSelectedRecipient(selectedUser);

    // Buscar conversacion existente en la lista local
    const existing = conversations.find(c => {
      const participants = c.participants || [];
      return participants.some(p => p.id === selectedUser.id);
    });

    if (existing) {
      setActiveChatId(existing.conversation_id);
    } else {
      // No hay conversacion aun, el chat se enviara sin conversationId
      setActiveChatId(null);
      setMessages([]);
    }
  }, [conversations]);

  // Usuarios filtrados por rol seleccionado
  const filteredUsers = roleGroups.find(r => r.role_id === selectedRoleId)?.users || [];

  const contextValue = {
    // Estado
    conversations,
    messages: activeChatId === 'global' ? globalMessages : messages,
    activeChatId,
    selectedRecipient,
    onlineUsers,
    typingUsers,
    roleGroups,
    currentUserRoles,
    selectedRoleId,
    filteredUsers,
    isLoadingConversations,
    isLoadingMessages,
    // Acciones
    setActiveChatId,
    setSelectedRecipient,
    setSelectedRoleId,
    sendMessage,
    editMessage,
    deleteMessage,
    emitTyping,
    selectUser,
    joinGlobalChat,
    leaveGlobalChat,
    loadConversations,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};